import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { api } from '../services/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Skeleton } from '../components/ui/skeleton';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { ArrowLeft, AlertTriangle, Shield, TrendingUp, DollarSign, Clock, Users, XCircle, BarChart3 } from 'lucide-react';

function MetricRow({ label, value, unavailable, sub }) {
  return (
    <div className="flex justify-between items-center py-2 border-b last:border-0">
      <span className="text-xs text-muted-foreground">{label}</span>
      {unavailable ? <span className="text-xs text-amber-600 italic">Not yet available</span>
        : <span className="text-sm font-medium">{value ?? '—'}</span>}
      {sub && !unavailable && <span className="text-[10px] text-muted-foreground ml-2">{sub}</span>}
    </div>
  );
}

function formatCents(c) { return c != null ? `$${(c / 100).toFixed(2)}` : '—'; }

function useApi(endpoint) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const fetch = useCallback(async () => {
    setLoading(true); setError(null);
    try { const res = await api.get(endpoint); setData(res.data); }
    catch (e) { setError(e.response?.data?.detail || 'Failed to load'); }
    finally { setLoading(false); }
  }, [endpoint]);
  useEffect(() => { fetch(); }, [fetch]);
  return { data, loading, error };
}

// === FAILURES PAGE ===
function FailuresDetail() {
  const ch = useApi('/revenue/analytics/failures');
  const vf = useApi('/revenue/analytics/governance/vendor-failures');

  return (
    <div className="space-y-4" data-testid="failures-detail">
      <Card className="border">
        <CardHeader className="py-2 px-4 border-b"><CardTitle className="text-sm flex items-center gap-2"><AlertTriangle className="h-4 w-4" />Workflow Failures</CardTitle></CardHeader>
        <CardContent className="p-4">
          {ch.loading ? <Skeleton className="h-20 w-full" /> : ch.error ? <div className="text-xs text-red-500">{ch.error}</div> : (
            <div>
              {Object.keys(ch.data?.clearinghouse || {}).length === 0 && Object.keys(ch.data?.delivery || {}).length === 0 && (ch.data?.external_handoffs || []).length === 0
                ? <div className="text-xs text-muted-foreground">No failure data available.</div>
                : <table className="w-full text-xs">
                  <thead><tr className="border-b bg-muted/20"><th className="px-3 py-1.5 text-left">Domain</th><th className="px-3 py-1.5 text-left">Failure Class</th><th className="px-3 py-1.5 text-right">Count</th></tr></thead>
                  <tbody>
                    {Object.entries(ch.data?.clearinghouse || {}).map(([cls, cnt]) => <tr key={`ch-${cls}`} className="border-b"><td className="px-3 py-1.5">Clearinghouse</td><td className="px-3 py-1.5">{cls}</td><td className="px-3 py-1.5 text-right font-mono text-red-600">{cnt}</td></tr>)}
                    {Object.entries(ch.data?.delivery || {}).map(([cls, cnt]) => <tr key={`del-${cls}`} className="border-b"><td className="px-3 py-1.5">Delivery</td><td className="px-3 py-1.5">{cls}</td><td className="px-3 py-1.5 text-right font-mono text-red-600">{cnt}</td></tr>)}
                    {(ch.data?.external_handoffs || []).map((f, i) => <tr key={i} className="border-b"><td className="px-3 py-1.5">{f.domain}</td><td className="px-3 py-1.5">{f.failure_class}</td><td className="px-3 py-1.5 text-right font-mono text-red-600">{f.count}</td></tr>)}
                  </tbody>
                </table>}
            </div>
          )}
        </CardContent>
      </Card>

      <Card className="border">
        <CardHeader className="py-2 px-4 border-b"><CardTitle className="text-sm flex items-center gap-2"><Shield className="h-4 w-4" />Vendor Failures</CardTitle></CardHeader>
        <CardContent className="p-4">
          {vf.loading ? <Skeleton className="h-20 w-full" /> : vf.error ? <div className="text-xs text-red-500">{vf.error}</div> : (
            <div>
              {(vf.data?.clearinghouse_failures?.length || 0) + (vf.data?.delivery_failures?.length || 0) + (vf.data?.external_handoff_failures?.length || 0) === 0
                ? <div className="text-xs text-muted-foreground">No vendor failures recorded.</div>
                : <table className="w-full text-xs">
                  <thead><tr className="border-b bg-muted/20"><th className="px-3 py-1.5 text-left">Vendor</th><th className="px-3 py-1.5 text-left">Type</th><th className="px-3 py-1.5 text-left">Class</th><th className="px-3 py-1.5 text-right">Count</th></tr></thead>
                  <tbody>
                    {(vf.data?.clearinghouse_failures || []).map((f, i) => <tr key={`ch-${i}`} className="border-b"><td className="px-3 py-1.5">{f.vendor}</td><td className="px-3 py-1.5">Clearinghouse</td><td className="px-3 py-1.5">{f.failure_class}</td><td className="px-3 py-1.5 text-right font-mono">{f.count}</td></tr>)}
                    {(vf.data?.delivery_failures || []).map((f, i) => <tr key={`del-${i}`} className="border-b"><td className="px-3 py-1.5">{f.provider}</td><td className="px-3 py-1.5">Delivery</td><td className="px-3 py-1.5">{f.failure_class}</td><td className="px-3 py-1.5 text-right font-mono">{f.count}</td></tr>)}
                    {(vf.data?.external_handoff_failures || []).map((f, i) => <tr key={`ext-${i}`} className="border-b"><td className="px-3 py-1.5">{f.vendor}</td><td className="px-3 py-1.5">{f.domain}</td><td className="px-3 py-1.5">{f.failure_class}</td><td className="px-3 py-1.5 text-right font-mono">{f.count}</td></tr>)}
                  </tbody>
                </table>}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

// === GOVERNANCE PAGE ===
function GovernanceDetail() {
  const ov = useApi('/revenue/analytics/governance/overrides');
  const pc = useApi('/revenue/analytics/governance/policy-changes');
  const sp = useApi('/revenue/analytics/governance/suppressions');

  return (
    <div className="space-y-4" data-testid="governance-detail">
      <Card className="border">
        <CardHeader className="py-2 px-4 border-b"><CardTitle className="text-sm">Manual Overrides ({ov.data?.total_overrides || 0})</CardTitle></CardHeader>
        <CardContent className="p-4">
          {ov.loading ? <Skeleton className="h-20 w-full" /> : ov.error ? <div className="text-xs text-red-500">{ov.error}</div> : (
            <div className="grid grid-cols-2 gap-4">
              <div><div className="text-xs font-medium mb-1">By Type</div>
                {Object.entries(ov.data?.by_type || {}).map(([t, c]) => <div key={t} className="flex justify-between text-xs py-0.5 border-b"><span>{t.replace(/_/g, ' ')}</span><span className="font-mono">{c}</span></div>)}
              </div>
              <div><div className="text-xs font-medium mb-1">By Role</div>
                {Object.entries(ov.data?.by_role || {}).map(([r, c]) => <div key={r} className="flex justify-between text-xs py-0.5 border-b"><span>{r}</span><span className="font-mono">{c}</span></div>)}
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      <Card className="border">
        <CardHeader className="py-2 px-4 border-b"><CardTitle className="text-sm">Policy Changes ({pc.data?.total_changes || 0})</CardTitle></CardHeader>
        <CardContent className="p-4">
          {pc.loading ? <Skeleton className="h-20 w-full" /> : (
            <div>
              {Object.entries(pc.data?.by_policy_type || {}).map(([t, d]) => (
                <div key={t} className="flex justify-between text-xs py-1 border-b"><span className="capitalize">{t.replace(/_/g, ' ')}</span><span className="font-mono">{d.count} changes</span></div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      <Card className="border">
        <CardHeader className="py-2 px-4 border-b"><CardTitle className="text-sm">Active Suppressions ({sp.data?.total_active || 0})</CardTitle></CardHeader>
        <CardContent className="p-4">
          {sp.loading ? <Skeleton className="h-20 w-full" /> : (
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
              {Object.entries(sp.data || {}).filter(([k]) => k !== 'total_active').map(([layer, counts]) => (
                <div key={layer} className="p-2 bg-muted/20 rounded text-center">
                  <div className="text-xs font-medium capitalize mb-1">{layer}</div>
                  {Object.entries(counts).map(([k, v]) => <div key={k} className="text-[10px]">{k}: <span className="font-mono font-medium">{v}</span></div>)}
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

// === FINANCIAL PAGE ===
function FinancialDetail() {
  const sp = useApi('/revenue/analytics/financial/statement-performance');
  const cp = useApi('/revenue/analytics/financial/collections-performance');
  const wo = useApi('/revenue/analytics/financial/writeoff');
  const tm = useApi('/revenue/analytics/financial/conversion-timing');

  return (
    <div className="space-y-4" data-testid="financial-detail">
      <Card className="border">
        <CardHeader className="py-2 px-4 border-b"><CardTitle className="text-sm flex items-center gap-2"><DollarSign className="h-4 w-4" />Statement Performance</CardTitle></CardHeader>
        <CardContent className="p-4">
          {sp.loading ? <Skeleton className="h-20 w-full" /> : (
            <div>
              <MetricRow label="Statements Sent" value={sp.data?.statements_sent} />
              <MetricRow label="Statements Voided" value={sp.data?.statements_voided} />
              <MetricRow label="Current Artifacts" value={sp.data?.artifacts_current} />
              <MetricRow label="Total Balance at Generation" value={formatCents(sp.data?.total_balance_at_generation_cents)} />
              <MetricRow label="Avg Balance per Statement" value={formatCents(sp.data?.avg_balance_per_statement_cents)} />
              <MetricRow label="Avg Days to Payment" unavailable={sp.data?.average_days_to_payment == null} />
            </div>
          )}
        </CardContent>
      </Card>

      <Card className="border">
        <CardHeader className="py-2 px-4 border-b"><CardTitle className="text-sm flex items-center gap-2"><Users className="h-4 w-4" />Collections Performance</CardTitle></CardHeader>
        <CardContent className="p-4">
          {cp.loading ? <Skeleton className="h-20 w-full" /> : (
            <div>
              <MetricRow label="Total Actions" value={cp.data?.total_actions} />
              <MetricRow label="Internal Collections" value={cp.data?.internal_collections} />
              <MetricRow label="Agency Handoffs" value={cp.data?.agency_handoffs} />
              <MetricRow label="Active" value={cp.data?.active} />
              <MetricRow label="Closed" value={cp.data?.closed} />
              <MetricRow label="Balance in Collections" value={formatCents(cp.data?.total_balance_in_collections_cents)} />
              <MetricRow label="Recovery Rate" unavailable={cp.data?.recovery_rate == null} />
            </div>
          )}
        </CardContent>
      </Card>

      <Card className="border">
        <CardHeader className="py-2 px-4 border-b"><CardTitle className="text-sm flex items-center gap-2"><XCircle className="h-4 w-4" />Write-Off Metrics</CardTitle></CardHeader>
        <CardContent className="p-4">
          {wo.loading ? <Skeleton className="h-20 w-full" /> : (
            <div>
              <MetricRow label="Total Write-Offs" value={wo.data?.total_writeoffs} />
              <MetricRow label="Total Amount" value={formatCents(wo.data?.total_writeoff_amount_cents)} />
              <MetricRow label="Avg Amount" value={formatCents(wo.data?.avg_writeoff_amount_cents)} />
              <MetricRow label="Write-Off Rate" value={wo.data?.writeoff_rate != null ? `${wo.data.writeoff_rate}%` : undefined} unavailable={wo.data?.writeoff_rate == null} />
              {wo.data?.by_reason_code && Object.entries(wo.data.by_reason_code).map(([code, d]) => (
                <MetricRow key={code} label={`Reason: ${code}`} value={`${d.count} (${formatCents(d.total_cents)})`} />
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      <Card className="border">
        <CardHeader className="py-2 px-4 border-b"><CardTitle className="text-sm flex items-center gap-2"><Clock className="h-4 w-4" />Conversion Timing</CardTitle></CardHeader>
        <CardContent className="p-4">
          {tm.loading ? <Skeleton className="h-20 w-full" /> : (
            <div>
              {Object.entries(tm.data?.conversion_timing || {}).map(([stage, t]) => (
                <MetricRow key={stage} label={stage.replace(/_/g, ' ')} value={`${t.avg_aging_days}d avg`} sub={`max ${t.max_aging_days}d (${t.count} cases)`} />
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

// === MAIN DRILLDOWN ROUTER ===
export default function RevenueAnalyticsDrilldown() {
  const navigate = useNavigate();
  const location = useLocation();
  const path = location.pathname;

  let tab = 'failures';
  if (path.includes('/governance')) tab = 'governance';
  else if (path.includes('/financial')) tab = 'financial';

  return (
    <div className="flex-1 overflow-auto p-4 space-y-4 max-w-6xl" data-testid="analytics-drilldown">
      <div className="flex items-center justify-between">
        <h1 className="text-xl font-semibold tracking-tight flex items-center gap-2"><BarChart3 className="h-5 w-5" />Analytics Detail</h1>
        <Button variant="ghost" size="sm" className="h-7 text-xs gap-1" onClick={() => navigate('/revenue/analytics')} data-testid="back-analytics"><ArrowLeft className="h-3 w-3" />Analytics Dashboard</Button>
      </div>

      <Tabs value={tab} onValueChange={v => navigate(`/revenue/analytics/${v}`)}>
        <TabsList className="bg-muted/30 p-1">
          <TabsTrigger value="failures" className="text-xs gap-1.5"><AlertTriangle className="h-3.5 w-3.5" />Failures</TabsTrigger>
          <TabsTrigger value="governance" className="text-xs gap-1.5"><Shield className="h-3.5 w-3.5" />Governance</TabsTrigger>
          <TabsTrigger value="financial" className="text-xs gap-1.5"><TrendingUp className="h-3.5 w-3.5" />Financial</TabsTrigger>
        </TabsList>
        <TabsContent value="failures"><FailuresDetail /></TabsContent>
        <TabsContent value="governance"><GovernanceDetail /></TabsContent>
        <TabsContent value="financial"><FinancialDetail /></TabsContent>
      </Tabs>
    </div>
  );
}
